
import { useState } from "react";
import { SupabaseConnectionTest } from "@/components/SupabaseConnectionTest";
import { Button } from "@/components/ui/button";
import { AlertCircle, ExternalLink, Database } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const Settings = () => {
  const [showDirectConnect, setShowDirectConnect] = useState(false);
  const [supabaseUrl, setSupabaseUrl] = useState("");
  const [supabaseKey, setSupabaseKey] = useState("");
  const { toast } = useToast();

  const setDirectConnection = () => {
    if (!supabaseUrl || !supabaseKey) {
      toast({
        title: "Missing Information",
        description: "Please enter both Supabase URL and anon key",
        variant: "destructive",
      });
      return;
    }

    // Set the values in localStorage to override environment variables
    localStorage.setItem("supabase_url", supabaseUrl);
    localStorage.setItem("supabase_key", supabaseKey);
    
    toast({
      title: "Connection Information Set",
      description: "Refresh the page to apply the new connection settings",
    });

    // Force reload to apply changes
    window.location.reload();
  };

  const clearDirectConnection = () => {
    localStorage.removeItem("supabase_url");
    localStorage.removeItem("supabase_key");
    
    toast({
      title: "Connection Settings Cleared",
      description: "Refresh the page to revert to environment variables",
    });

    // Force reload to apply changes
    window.location.reload();
  };

  const hasLocalStorageConnection = localStorage.getItem("supabase_url") && localStorage.getItem("supabase_key");

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <div className="grid grid-cols-1 gap-8">
        <section>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Supabase Connection</h2>
            <div className="flex gap-2">
              {hasLocalStorageConnection && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={clearDirectConnection}
                >
                  Clear Browser Connection
                </Button>
              )}
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => setShowDirectConnect(true)}
              >
                <Database className="h-4 w-4 mr-2" />
                Set Direct Connection
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.open("https://dash.cloudflare.com/", "_blank")}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Cloudflare Dashboard
              </Button>
            </div>
          </div>
          
          {hasLocalStorageConnection && (
            <Alert className="mb-4">
              <Database className="h-4 w-4" />
              <AlertTitle>Using Browser Connection</AlertTitle>
              <AlertDescription>
                You are currently using Supabase credentials stored in your browser.
                These override any Cloudflare environment variables.
              </AlertDescription>
            </Alert>
          )}
          
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Configuration Required</AlertTitle>
            <AlertDescription>
              <p className="mb-2">
                To use Supabase with this application, you need to set the following environment variables in your Cloudflare Pages project:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 text-sm">
                <li><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">VITE_SUPABASE_URL</code> - Your Supabase project URL</li>
                <li><code className="bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">VITE_SUPABASE_ANON_KEY</code> - Your Supabase anonymous key</li>
              </ul>
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium">How to set up environment variables in Cloudflare Pages:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2 text-sm">
                  <li>Go to Cloudflare Dashboard</li>
                  <li>Select your Pages project</li>
                  <li>Navigate to Settings &gt; Environment variables</li>
                  <li>Add the variables above with your Supabase credentials</li>
                  <li>Make sure to change the type from "Text" to "Secret" for security</li>
                  <li>Make sure to add them to both Production and Preview environments</li>
                  <li>Save and redeploy your application</li>
                </ol>
              </div>
              <Alert className="mt-4" variant="warning">
                <Info className="h-4 w-4" />
                <AlertTitle>Cloudflare Limitation</AlertTitle>
                <AlertDescription className="text-xs">
                  Cloudflare Pages sometimes has trouble with environment variables. If you've added them but they're not
                  working, please use the "Set Direct Connection" option above as a workaround.
                </AlertDescription>
              </Alert>
            </AlertDescription>
          </Alert>
          
          <SupabaseConnectionTest />
        </section>
        
        {/* Other settings sections can be added here */}
      </div>

      <Dialog open={showDirectConnect} onOpenChange={setShowDirectConnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect Directly to Supabase</DialogTitle>
            <DialogDescription>
              Enter your Supabase project URL and anonymous key to connect directly from this browser.
              This is useful for testing and development.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="supabase-url" className="text-sm font-medium">
                Supabase URL
              </label>
              <Input
                id="supabase-url"
                placeholder="https://your-project-id.supabase.co"
                value={supabaseUrl}
                onChange={(e) => setSupabaseUrl(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <label htmlFor="supabase-key" className="text-sm font-medium">
                Supabase Anon Key
              </label>
              <Input
                id="supabase-key"
                placeholder="your-anon-key"
                value={supabaseKey}
                onChange={(e) => setSupabaseKey(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDirectConnect(false)}>
              Cancel
            </Button>
            <Button onClick={setDirectConnection}>
              Connect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
